import * as React from 'react'
import type { VendorSnapshot } from '@/lib/api'
import type { SocialApp } from '@/lib/utils'
import { event, getDeviceType, getPageInfo } from '@/lib/analytics'
import { trackCtaClick } from '@/lib/backendTracking'

export type ContactOptions = {
  message: string
  whatsapp?: string
  channelOverride?: SocialApp
  tripSlug?: string
}

export type AppState = {
  tenant: string
  socialApp: SocialApp
  snap: VendorSnapshot | null
  contact: (opts: ContactOptions) => void
}

const AppStateContext = React.createContext<AppState | undefined>(undefined)

export function AppStateProvider({ tenant, socialApp, snap, children }:
  { tenant: string; socialApp: SocialApp; snap: VendorSnapshot | null; children: React.ReactNode }) {
  function buildWhatsAppUrl(input: string | undefined, message: string): string | undefined {
    if (!input) return undefined
    const encoded = encodeURIComponent(message)
    const val = input.trim()
    if (/^https?:\/\//i.test(val)) {
      // Full URL provided; append text param preserving existing query
      try {
        const u = new URL(val)
        if (!u.searchParams.has('text')) {
          u.searchParams.append('text', message)
        }
        return u.toString()
      } catch {
        // Fallback to as-is if URL parsing fails
        return val
      }
    }
    // Treat as phone; keep digits and plus
    const digits = val.replace(/[^0-9+]/g, '')
    if (!digits) return undefined
    return `https://wa.me/${digits}?text=${encoded}`
  }

  const contact = React.useCallback((opts: ContactOptions) => {
    // Track GA4 event (SSR-safe: getPageInfo returns null on server)
    const pageInfo = getPageInfo()
    if (pageInfo) {
      event('contact_click', {
        page_path: pageInfo.pagePath,
        page_url: pageInfo.fullUrl,
        click_timestamp: pageInfo.timestamp,
        device_type: getDeviceType(),
      })
    }

    // Track backend CTA click
    trackCtaClick(opts.tripSlug)

    const targetWhatsappUrl = buildWhatsAppUrl(opts.whatsapp || snap?.socials?.whatsapp, opts.message)

    const websiteUrl = snap?.socials?.website
    const emailAddr = snap?.socials?.email
    const phoneNum = snap?.socials?.phone?.replace(/[^0-9+]/g, '')

    let url: string | undefined
    // Always prioritize WhatsApp first, regardless of source platform
    if (targetWhatsappUrl) {
      url = targetWhatsappUrl
    } else if (emailAddr) {
      url = `mailto:${emailAddr}?subject=${encodeURIComponent('Trip inquiry')}&body=${encodeURIComponent(opts.message)}`
    } else if (phoneNum) {
      url = `tel:${phoneNum}`
    } else if (websiteUrl) {
      url = websiteUrl
    }

    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }, [snap])

  const value = React.useMemo<AppState>(() => ({ tenant, socialApp, snap, contact }), [tenant, socialApp, snap, contact])
  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
}

export function useAppState() {
  const ctx = React.useContext(AppStateContext)
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider')
  return ctx
}


